import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, HeartPulse, Users, ArrowRight, ShieldCheck } from 'lucide-react';
import { STATS } from '../mock/mock';

const VALUES = [
  { icon: HeartPulse, title: 'Patient First', text: 'Every decision is driven by the best possible patient outcome.' },
  { icon: ShieldCheck, title: 'Field Proven', text: 'We only supply and use gear we\u2019d trust with our own lives.' },
  { icon: Users, title: 'Real Medics', text: 'Our team brings genuine frontline pre-hospital experience.' },
  { icon: Target, title: 'Zero Preventable Deaths', text: 'Our mission guides everything we do, every single day.' },
];

const About = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div>
      <div className="relative bg-[#141414] text-white py-20 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1579037005241-a79202c7e9fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141414] to-[#141414]/60" />
        <div className="container-fes relative">
          <span className="section-label">About Us</span>
          <h1 className="font-head text-4xl md:text-5xl uppercase mt-2 max-w-2xl">Redefining Prehospital Emergency Care</h1>
        </div>
      </div>

      <section className="py-16 md:py-20 bg-white">
        <div className="container-fes grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-lg overflow-hidden">
            <img src="https://images.pexels.com/photos/6519905/pexels-photo-6519905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000" alt="FesMed medics" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="section-label">Our Story</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2 mb-5">Built by Medics, for Medics</h2>
            <p className="text-gray-600 mb-4 leading-relaxed">FesMed EMS was founded on a simple belief: high-quality emergency care and equipment should be accessible to everyone — from frontline responders to everyday Australians.</p>
            <p className="text-gray-600 leading-relaxed">We combine a curated range of field-proven medical supplies with professional event coverage and accredited training. Whether we are equipping an IFAK, covering a remote festival or teaching bystanders to stop the bleed, our focus never changes: saving lives.</p>
            <Link to="/contact" className="btn-navy mt-8">Work With Us <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="py-14 bg-[#141414] text-white">
        <div className="container-fes grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-head text-4xl md:text-5xl text-[#ef4444]">{s.value}</div>
              <div className="font-cond uppercase tracking-wide text-gray-300 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#f4f6fa]">
        <div className="container-fes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            <div className="bg-white rounded-lg p-8 hover-lift">
              <Target size={32} className="text-[#c8102e] mb-4" />
              <h3 className="font-head text-2xl uppercase text-[#141414] mb-2">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">Zero preventable deaths. We equip, train and support responders and communities to deliver decisive care when seconds matter.</p>
            </div>
            <div className="bg-white rounded-lg p-8 hover-lift">
              <Eye size={32} className="text-[#c8102e] mb-4" />
              <h3 className="font-head text-2xl uppercase text-[#141414] mb-2">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">A future where every person has the gear, knowledge and confidence to act — turning bystanders into life-savers.</p>
            </div>
          </div>

          <div className="text-center mb-12">
            <span className="section-label">Our Values</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-6 text-center hover-lift">
                <div className="w-14 h-14 rounded-full bg-[#f4f6fa] flex items-center justify-center text-[#c8102e] mx-auto mb-4"><v.icon size={26} /></div>
                <h4 className="font-head text-lg uppercase text-[#141414] mb-2">{v.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
